import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Zap, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";

const LS_DISMISS_KEY = "pfo_inline_upgrade_pill_dismissed_v1";

/**
 * InlineUpgradePill — soft, one-tap upgrade path for users who
 * dismissed the trial-ending modal.
 *
 * Shows a compact "OMR 1/mo — Upgrade" chip at the top of the
 * Dashboard for **non-admin, non-Pro trial users who dismissed at
 * least one trial-ending modal threshold**. The pill honours a
 * separate localStorage flag so the user can hide it too. When both
 * are dismissed we've asked twice — we stop nagging.
 *
 * Rationale: after the modal is dismissed, the only path back to
 * Pricing is the sidebar CTA, which is invisible on mobile behind
 * the More sheet. This pill gives the same reachability on the
 * primary screen without another blocking modal.
 */
export default function InlineUpgradePill() {
  const { t } = useTranslation();
  const { user, trialStatus } = useAuth();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(LS_DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  if (hidden) return null;
  if (!user || user.is_admin) return null;
  if (!trialStatus || trialStatus.is_pro) return null;

  // Only show once the user has dismissed at least one modal
  // threshold — signals they've explicitly said "not now" and don't
  // need the full modal again but might still want a nudge.
  const anyDismissed = (() => {
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith("pfo_trial_end_shown_v1_") && localStorage.getItem(key) === "1") {
          return true;
        }
      }
    } catch { /* private mode */ }
    return false;
  })();
  if (!anyDismissed) return null;

  // 6-month trial ⇒ 180 days at the start. If the user is past 90 days
  // (i.e., days_left ≤ 90), they're a "long-consideration" user — the
  // annual plan's per-month price is a stronger anchor than the monthly
  // one because they've clearly weighed us over months, not days.
  //
  // Users still in the first 90 days see the monthly copy (lower
  // commitment, easier "yes"). Both variants link to /pricing.
  const daysLeft = Number(trialStatus.days_left);
  const showAnnualVariant = Number.isFinite(daysLeft) && daysLeft <= 90 && daysLeft >= 0;

  const label = Number.isFinite(daysLeft) && daysLeft >= 0
    ? (showAnnualVariant
        ? t("upgrade.pill_annual_hero", {
            count: daysLeft,
            defaultValue: `${daysLeft} days left — Save 2 OMR with Pro Annual`,
          })
        : t("upgrade.pill_days_left", {
            count: daysLeft,
            defaultValue: `${daysLeft} days left in trial — Upgrade to Pro`,
          }))
    : t("upgrade.pill_default", { defaultValue: "Upgrade to Pro" });

  const hint = showAnnualVariant
    ? t("upgrade.pill_annual_hint", { defaultValue: "OMR 10/year (≈ 0.83/month) · Cancel anytime" })
    : t("upgrade.pill_hint", { defaultValue: "OMR 1/month · Cancel anytime" });

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(LS_DISMISS_KEY, "1"); } catch { /* private mode */ }
    setHidden(true);
  };

  return (
    <button
      onClick={() => navigate("/pricing")}
      data-testid="inline-upgrade-pill"
      className="w-full mb-4 group flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all hover:shadow-sm text-left animate-fadeup"
      style={{
        background: "linear-gradient(90deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.10) 100%)",
        borderColor: "rgba(224,158,80,0.38)",
      }}
    >
      <div
        className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
        style={{ background: "var(--accent-warm)" }}
      >
        <Zap size={14} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
          {label}
        </div>
        <div className="text-[11px] text-[var(--text-muted)] truncate">
          {hint}
        </div>
      </div>
      <span
        className="hidden sm:inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-md whitespace-nowrap"
        style={{ background: "rgba(224,158,80,0.16)", color: "var(--accent-warm)" }}
      >
        {t("upgrade.pill_cta", { defaultValue: "Upgrade" })} →
      </span>
      <span
        onClick={handleDismiss}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleDismiss(e); }}
        aria-label={t("upgrade.pill_dismiss", { defaultValue: "Dismiss upgrade pill" })}
        data-testid="inline-upgrade-pill-dismiss"
        className="h-6 w-6 rounded flex items-center justify-center text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text-primary)] transition-colors shrink-0"
      >
        <X size={12} />
      </span>
    </button>
  );
}
